<script setup lang="ts">
import { computed } from 'vue'

interface IssueFilters {
  hasHolds: boolean | undefined
  notAvailableForPickup: boolean | undefined
  violatesReturnRules: boolean | undefined
  missingLicensePlate: boolean | undefined
  requiresCheckIn: boolean | undefined
}

interface IssueOption {
  key: keyof IssueFilters
  label: string
  description: string
  value: keyof IssueFilters
}

const props = defineProps<{
  modelValue: IssueFilters
}>()

const emit = defineEmits<{
  (event: 'update:modelValue', filters: IssueFilters): void
}>()

const issueOptions: IssueOption[] = [
  {
    key: 'hasHolds',
    value: 'hasHolds',
    label: 'Holds',
    description: 'Show Import Appointments with Holds',
  },
  {
    key: 'notAvailableForPickup',
    value: 'notAvailableForPickup',
    label: 'Not ready for pickup',
    description: 'Show Import Appointments Not Available for Pickup',
  },
  {
    key: 'violatesReturnRules',
    value: 'violatesReturnRules',
    label: 'Return-rule violation',
    description: 'Show Empty Appointments Violating Return Rules',
  },
  {
    key: 'missingLicensePlate',
    value: 'missingLicensePlate',
    label: 'Missing license plate',
    description: 'Show Appointments Missing Required License Plate',
  },
  {
    key: 'requiresCheckIn',
    value: 'requiresCheckIn',
    label: 'Requires check-in',
    description: 'Show Import Appointments Requiring Check-in',
  },
]

const hasAllIssuesSelected = computed(() => {
  return Object.values(props.modelValue).every(Boolean)
})

const selectedValues = computed({
  get: () => {
    return issueOptions
      .filter((option) => props.modelValue[option.key])
      .map((option) => option.value)
  },
  set: (values: (keyof IssueFilters)[]) => {
    const newFilters = { ...props.modelValue }
    // Clear all first
    issueOptions.forEach((option) => {
      newFilters[option.key] = undefined
    })
    // Set selected ones
    values.forEach((value) => {
      newFilters[value] = true
    })
    emit('update:modelValue', newFilters)
  },
})

const handleAllOptionClick = () => {
  if (hasAllIssuesSelected.value) {
    // If all issues are selected, clear all
    const newFilters = { ...props.modelValue }
    issueOptions.forEach((option) => {
      newFilters[option.key] = undefined
    })
    emit('update:modelValue', newFilters)
  } else {
    // If not all issues are selected, select all
    const newFilters = { ...props.modelValue }
    issueOptions.forEach((option) => {
      newFilters[option.key] = true
    })
    emit('update:modelValue', newFilters)
  }
}
</script>

<template>
  <el-select
    v-model="selectedValues"
    multiple
    filterable
    default-first-option
    collapse-tags
    collapse-tags-tooltip
    clearable
    placeholder="Select issues to filter by"
    size="large"
    class="w-full"
  >
    <!-- All option -->
    <el-option
      key="all"
      :label="hasAllIssuesSelected ? 'Clear All' : 'Select All'"
      value="__all__"
      @click="handleAllOptionClick"
    >
      <div class="issue-option">
        <i-mdi:check-all />
        <span>{{ hasAllIssuesSelected ? 'Clear All' : 'Select All' }}</span>
      </div>
    </el-option>

    <!-- Individual issue options -->
    <el-option
      v-for="option in issueOptions"
      :key="option.key"
      :label="option.label"
      :value="option.value"
      :title="option.description"
    >
      <div class="issue-option">
        <IssueIcon :issue="option.key" />
        <span>{{ option.label }}</span>
      </div>
    </el-option>
  </el-select>
</template>

<style lang="scss" scoped>
.issue-option {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
